/**
 * Alpine.js Speakers Language Switcher
 *
 * Lets users flip between their learning languages inside the
 * "{Language} Speakers" widget on the account/home page. Fetches a
 * freshly-rendered widget from /app/helpers/usersets and swaps the
 * content in place.
 */
export function speakersLanguageSwitcher(initialLang, layout = 'modern', canSwitchLanguage = false) {
    return {
        activeLang: (initialLang || '').toUpperCase(),
        layout: layout || 'modern',
        canSwitchLanguage: Boolean(canSwitchLanguage),
        loading: false,
        error: false,
        menuOpen: false,
        menuOpener: null,

        // The sheet sits before the fragment in the DOM, so focus moves into it on open.
        openMenu(opener = null) {
            this.menuOpener = opener;
            this.menuOpen = true;
            this.$nextTick(() => {
                const sheet = document.getElementById('speakers-language-menu');
                const option = sheet?.querySelector('[data-speakers-language-option][data-active]')
                    || sheet?.querySelector('[data-speakers-language-option]');
                option?.focus();
            });
        },

        closeMenu() {
            this.menuOpen = false;
            if (this.menuOpener?.isConnected) {
                this.menuOpener.focus();
            }
            this.menuOpener = null;
        },

        // Arrow keys move focus through the radio group. Enter or Space selects.
        moveOption(event, step) {
            const options = [...event.currentTarget.parentElement.querySelectorAll('[data-speakers-language-option]')];
            const index = options.indexOf(event.currentTarget);
            options[(index + step + options.length) % options.length]?.focus();
        },

        isActive(lang) {
            return this.activeLang === (lang || '').toUpperCase();
        },

        async switchTo(lang) {
            const code = (lang || '').toUpperCase();
            if (!code || this.loading || this.isActive(code)) return;

            const target = this.$refs.widget;
            if (!target) return;

            const previous = this.activeLang;
            this.activeLang = code;
            this.loading = true;
            this.error = false;

            try {
                const layoutParam = this.layout === 'legacy' ? '&layout=ultra-legacy' : '';
                const url = '/app/helpers/usersets?todo=lang&render=1&limit=20&lang=' + encodeURIComponent(code) + layoutParam;
                const response = await fetch(url, { credentials: 'include' });
                if (!response.ok) throw new Error('HTTP ' + response.status);
                const html = await response.text();

                target.innerHTML = html;

                if (window.Alpine && window.Alpine.initTree) {
                    window.Alpine.initTree(target);
                }
                // The swap removes the trigger that closeMenu() focused. Alpine drops
                // x-cloak from the new trigger after initTree, so focus waits a tick.
                this.$nextTick(() => {
                    if (document.activeElement === document.body) {
                        target.querySelector('[data-speakers-language-action]')?.focus();
                    }
                });
                if (window.LucideIcons) {
                    window.LucideIcons.initialize(target);
                } else if (window.lucide) {
                    window.lucide.createIcons({ parent: target });
                }
            } catch (e) {
                this.activeLang = previous;
                this.error = true;
            } finally {
                this.loading = false;
            }
        }
    };
}
